// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, Drawer, Fab, IconButton, Menu, MenuItem, Snackbar, TextField, Tooltip as MuiTooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import SendIcon from '@mui/icons-material/Send';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { matchPath } from 'react-router-dom';
import { RootState } from 'Store';
import { PROJECT_ESRI_3D_MAP_ROUTE } from 'Utils/routes';
import { useAppDispatch, useAppSelector, useAppStore } from 'Hooks';
import { pinWidget, unpinWidget, pinChart, unpinChart, setChatbotOpen, setChatHighlight, setChatQueryResult, setChatPano, setSelectedRegion, setRegionCommand, setFeatureAnalysis, setFeatureAnalysisOnDashboard, removeFeatureAnalysis, resetDashboardArtifacts, PinnedChart, HighlightTarget, FeatureAnalysis } from 'Features/dashboard/dashboardSlice';
import { addLayer, clearLayers, ViewerLayer } from 'Features/EsriSplitViewer/viewerLayersSlice';
import { WidgetKind, WidgetOperation, WidgetSpec, widgetPreviewData } from 'Features/dashboard/dashboardMetrics';
import WidgetView from 'Features/dashboard/WidgetView';
import stylesDef from './styles';
import CHATBOT_DRAWER_WIDTH, { isKhoraUiEnabled, KHORA_ICON_URL, KHORA_TOUR_VIDEO_URL } from './constants';

// The Khora icon + tour video are served from S3 (content.airworks.io), not bundled — see constants.
const khoraIcon = KHORA_ICON_URL;
const tourVideo = KHORA_TOUR_VIDEO_URL;
import { useChatbot, ChatAppState, ChatEndpoint, ChatWidget, ChatHighlight, ChatSpatialLayer, ChatFeatureAnalysis, ChatS3Ref, ChatChartSpec, ChatImageBlock, ChatTextBlock, ChatMessage, ChatFile, ChatPanoCommand, ChatAppAction, ChatQueryResult, StoryboardScene } from './useChatbot';
import ThinkingIndicator from './ThinkingIndicator';
import ArtifactErrorBoundary from 'Components/ArtifactErrorBoundary';
import ChatChart from './ChatChart';
import ChatCard from './ChatCard';
import ChatAnswerTrust from './ChatAnswerTrust';
import StoryboardPlayer, { STORYBOARD_STATUS_COLORS } from './StoryboardPlayer';
import ReTensionSlider from './ReTensionSlider';
import ChatMarkdown from './ChatMarkdown';
import { exportChat, ExportFormat } from './chatExport';
import { resolveChatOrder, buildOrderContext } from './chatOrder';
import isTrustedDownloadUrl from './downloadUrl';
import { captureCurrentPanoFrame } from './panoImage';
import { fileToAttachment, Attachment, isAttachmentError } from './uploadAttachment';

// Derive the chat's app-state snapshot from the LATEST store, so it reflects any actions taken
// between messages (e.g. clicking a different feature). Called at send time.
const buildChatAppState = (state: RootState, pathname: string): ChatAppState => {
  // Output (gdb) is available only ON THE PROJECT VIEWER PAGE once the order is placed and its
  // geodatabase is published — being on that page is a HARD requirement (the gdb is only loaded on
  // the map there, so show_on_map/highlight only work there). The gdb lives on the PLACED order the
  // viewer loaded (orderSelected), NOT on activeOrder (the unplaced draft, orderStatus 0). Mirror the
  // viewer's own gate: a geoDbFile with esriUrl + esriError 'success'. ?gdbOutput=0/1 only overrides
  // the publish check for local testing — it can NOT bypass the Project Viewer page requirement.
  const inProjectViewer = Boolean(matchPath({ path: PROJECT_ESRI_3D_MAP_ROUTE }, pathname));
  const outputParam = new URLSearchParams(window.location.search).get('gdbOutput');
  // Resolve the viewed order through the SAME helper the chat endpoint + session key use
  // (resolveChatOrder), so app_state.output_available can't describe a different order than the one
  // the turn is actually sent to (the endpoint falls through to placed[0]; this must too).
  const viewedOrder = resolveChatOrder(state);
  const orderHasOutput = (viewedOrder?.geoDbFiles ?? [])
    .some((file) => file.esriUrl && String(file.esriError ?? '').toLowerCase() === 'success');
  const outputAvailable = inProjectViewer && (outputParam !== null ? outputParam !== '0' : orderHasOutput);

  return {
    hasActiveProject: Boolean(state.project.project),
    output_available: outputAvailable,
    // The gdb feature clicked in the viewer (output phase only) — Khora resolves "this feature".
    selected_feature: outputAvailable ? (state.dashboard.selectedGdbFeature ?? undefined) : undefined,
    // A region the user drew on the viewer (output phase only) — Khora scopes "in this area" to it (query_region).
    selected_region: outputAvailable ? (state.dashboard.selectedRegion ?? undefined) : undefined,
  };
};

// A chart-CLICK highlight lives this long (matches the setChatHighlight ttlMs). Hovering another bar
// within this window is ignored, so an accidental hover can't steal or clear the deliberate selection.
const CHART_HIGHLIGHT_TTL_MS = 5000;

// Stable id for a highlight the user pins as an Analysis Layer — dedups so re-pinning the same result
// (verify re-emit, scrolling back) replaces in place instead of stacking duplicate scene layers.
const highlightLayerId = (highlight: ChatHighlight, messageId: string, index: number): string => (
  `hl:${messageId}:${index}:${highlight.target_layer ?? highlight.label ?? ''}`
);

// GeoJSON geometry type → a friendly suffix for the per-type Analysis Layer (a mixed collection is split).
const GEOM_TYPE_LABEL: Record<string, string> = {
  Point: 'points', MultiPoint: 'points', LineString: 'lines', MultiLineString: 'lines',
  Polygon: 'areas', MultiPolygon: 'areas',
};

// A keyless (pinned-highlight) layer stores its geojson INLINE — unlike a keyed add_spatial_layer, which
// offloads to S3. Above this size we DON'T persist it into the session, so one huge pin can't bloat the
// fire-and-forget session-save past the body limit (which would silently drop the whole session). The pin
// stays live for the session; it just won't survive a reload.
const MAX_PERSISTED_INLINE_LAYER_BYTES = 512 * 1024;

const ChatbotLauncher = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  // Open/closed lives in Redux (dashboard.chatbotOpen) — the single source of truth shared with the
  // Shell (content-shrink margin) and the dashboard panel (slides left), which render in other trees.
  const open = useAppSelector((state) => state.dashboard.chatbotOpen);
  const setOpen = useCallback((value: boolean) => { dispatch(setChatbotOpen(value)); }, [dispatch]);
  // On unmount (leaving the Project Viewer), close the chat so nothing anchored to it sticks.
  useEffect(() => () => { dispatch(setChatbotOpen(false)); }, [dispatch]);
  // Shift the app frame beside the drawer via a CSS variable: the Shell carries only a passive
  // `margin-right: var(--khora-drawer-offset, 0px)` rule (see shell/styles.ts) — no Khora logic
  // there, and toggling the chat re-renders nothing outside this component and the dashboard.
  useEffect(() => {
    document.documentElement.style.setProperty('--khora-drawer-offset', open ? CHATBOT_DRAWER_WIDTH : '0px');
    return () => { document.documentElement.style.removeProperty('--khora-drawer-offset'); };
  }, [open]);
  const [draft, setDraft] = useState('');
  // A pending upload (a site photo or PDF page) the NEXT send will attach — Khora answers about it with vision,
  // cross-referencing the gdb. An explicit attachment overrides the pano-vision auto-capture for that turn.
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [attachBusy, setAttachBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Download menu: which messages to export (whole session or one response) + where to anchor it.
  const [exportMenu, setExportMenu] = useState<{ anchor: HTMLElement; messages: ChatMessage[]; scope: 'session' | 'message' } | null>(null);
  // Clear-chat confirm: anchor for the little "Clear conversation" menu (two-click, so no accidental wipe).
  const [clearMenuEl, setClearMenuEl] = useState<HTMLElement | null>(null);
  // The Khora quick-tour video modal (opened from the chat header or the empty state).
  const [tourOpen, setTourOpen] = useState(false);

  // The app-state snapshot is read fresh from the store at send time, so actions taken between
  // messages — e.g. clicking a different gdb feature — are reflected.
  const store = useAppStore();
  const buildAppState = useCallback(
    () => buildChatAppState(store.getState(), window.location.pathname),
    [store],
  );

  // Resolve the backend endpoint at send time: the aw-server gdb-chat route (+ Okta token), or
  // undefined (chat disabled) when the order context can't be built. The route's :oid must be the
  // PLACED order being viewed (which owns the gdb) — mirror buildChatAppState's resolution.
  const getChatEndpoint = useCallback((): ChatEndpoint | undefined => {
    const ctx = buildOrderContext(store.getState());
    if (!ctx) return undefined;
    // gdbId = the geodatabase version LOADED in the viewer: the chat targets that exact version and
    // sessions are stored per version (open v3 -> chat about v3, its own thread; switch to v5 -> v5's).
    const { loadedGdbId } = store.getState().dashboard ?? {};
    return {
      url: `${ctx.base}/orders/${ctx.orgId}/${ctx.projectId}/${ctx.orderId}/gdb-chat`,
      token: ctx.token,
      gdbId: loadedGdbId ?? undefined,
    };
  }, [store]);

  // Stable id for a widget spec (deduped by its operation + layers + fields + FILTER + display_unit), so
  // the pin toggle + restore can find it. The filter is part of the identity — 'total poles' and 'poles
  // under 30 ft' must be DIFFERENT widgets, not one pin controlled by both. display_unit is too: pinWidget
  // ignores id collisions, so without it 'length in meters' could never pin once 'length in feet' is pinned.
  const widgetSpecId = (widget: ChatWidget): string => {
    const filterKey = widget.filter ? `${widget.filter.field}${widget.filter.op}${widget.filter.value}` : '';
    return `${widget.operation}:${widget.layers.join(',')}:${widget.by_field ?? ''}:${widget.field ?? ''}:${filterKey}:${widget.display_unit ?? ''}`;
  };
  // Pin a Khora-defined dashboard widget into the shared dashboard slice (deduped by spec), user-initiated.
  const handlePinWidget = useCallback((widget: ChatWidget) => {
    dispatch(pinWidget({
      id: widgetSpecId(widget),
      title: widget.title,
      kind: widget.kind as WidgetKind,
      operation: widget.operation as WidgetOperation,
      layers: widget.layers,
      byField: widget.by_field,
      field: widget.field,
      filter: widget.filter,
      derivation: widget.derivation,
      displayUnit: widget.display_unit,
      // Keep the Lambda's whole-gdb snapshot so the pinned widget shows the value Khora reported in
      // chat (view-scoped mode still recomputes live) — see WidgetSpec.preview.
      preview: widget.preview,
    }));
  }, [dispatch]);
  const handleUnpinWidget = useCallback((id: string) => { dispatch(unpinWidget(id)); }, [dispatch]);

  // Pin a make_chart (recharts) chart onto the dashboard as the EXACT chart shown in the chat, so the
  // two surfaces render identically (same ChatChart component, no recompute). Deduped by id.
  const handlePinChart = useCallback((id: string, chart: ChatChartSpec) => {
    dispatch(pinChart({ id, chart }));
  }, [dispatch]);

  // Pinned state, so each chart "Pin to dashboard" button toggles to "Unpin from dashboard".
  const pinnedCharts = useAppSelector((state) => state.dashboard?.pinnedCharts ?? []);
  // Per-feature analyses, so the bubble's "Add to Dashboard" control reflects whether each is pinned.
  const featureAnalysis = useAppSelector((state) => state.dashboard?.featureAnalysis ?? {});
  // Pinned widgets, so each widget candidate's "Pin as widget" button reflects whether it's on the board.
  const pinnedWidgets = useAppSelector((state) => state.dashboard?.pinnedWidgets ?? []);
  // Current slider stop per parametric chart (by pinId), so "Pin to dashboard" captures the frame the
  // user settled on — a static chart at that value, never the whole sweep.
  const chartFrameRef = useRef<Record<string, number>>({});
  // Timestamp (ms) until which a chart CLICK owns the highlight; hover events on other bars are ignored
  // until then, so an accidental hover can't override the deliberate 5s selection.
  const hoverLockUntilRef = useRef(0);
  // The current order/gdb session identity, mirrored into a ref (assigned once sessionKey is computed
  // below) so async handlers can detect an order switch that happened while they were awaiting.
  const sessionKeyRef = useRef<string | undefined>(undefined);
  const handleUnpinChart = useCallback((id: string) => { dispatch(unpinChart(id)); }, [dispatch]);

  // Download a Khora-produced file: inline b64 for a small one, or its presigned S3 URL for a large
  // offloaded export (fetched into a blob so the download keeps its filename even cross-origin). The
  // URL is validated against the verified-host allow-list before any fetch; an untrusted url falls
  // through to the inline b64, or the download is skipped.
  // A download that resolves to neither a usable URL nor inline bytes, or a fetch that fails, used to be a
  // SILENT no-op — the user clicked and nothing happened. Surface a brief reason instead.
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const handleDownloadFile = useCallback(async (file: ChatFile) => {
    const anchor = document.createElement('a');
    let objectUrl: string | null = null;
    try {
      if (file.url && isTrustedDownloadUrl(file.url)) {
        const response = await fetch(file.url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        anchor.href = objectUrl;
      } else if (file.b64) {
        anchor.href = `data:${file.mime};base64,${file.b64}`;
      } else {
        setDownloadError(`Couldn't download “${file.name}” — its link isn't ready yet. Try again in a moment.`);
        return;
      }
      anchor.download = file.name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      setDownloadError(`Couldn't download “${file.name}”. Please try again.`);
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  }, []);

  // The last map highlight stays in Redux after its 5s auto-clear, so a "Highlight again" chip can
  // re-run it (a new id retriggers the viewer effect → another 5s of highlight/pulse).
  const chatHighlight = useAppSelector((state) => state.dashboard.chatHighlight);
  // Opt-in pano vision: true while the pano viewer's toggle is on and a pano is open. Each question then
  // rides with a snapshot of the CURRENT pano view (captured at send) so Khora can analyze what's on the pole.
  const panoVisionActive = useAppSelector((state) => state.dashboard.panoVisionActive);
  const handleReHighlight = useCallback(() => {
    if (chatHighlight) dispatch(setChatHighlight({ ...chatHighlight, id: Date.now() }));
  }, [chatHighlight, dispatch]);

  // show_on_map: hand the features to the Project Viewer (via Redux) to render + tour. Merge all of a
  // turn's highlight sets into one FeatureCollection; tour if any set asked for a tour/flythrough.
  const handleHighlights = useCallback((highlights: ChatHighlight[]) => {
    // Prefer highlighting the ALREADY-DISPLAYED features in place: a set that carries a layer + join key
    // becomes a target the viewer queries + highlights at its real geometry (correct height, no float).
    // A set WITHOUT a key (e.g. a multi-layer query) falls back to drawing its geojson.
    const hasTarget = (highlight: ChatHighlight): boolean => (
      !!highlight.target_layer && !!highlight.key_field && (highlight.key_values?.length ?? 0) > 0
    );
    const targets: HighlightTarget[] = highlights.filter(hasTarget).map((highlight) => ({
      layer: highlight.target_layer as string,
      keyField: highlight.key_field as string,
      keyValues: highlight.key_values as string[],
      color: highlight.color,
      geojson: highlight.geojson, // drawn only if the in-place query for this target resolves nothing
    }));
    // Only UNtargeted sets (no key) fall back to a drawn geojson; targeted sets highlight in place. Keep
    // just the untargeted features here so a mixed turn (some keyed, some not) draws the keyless set too
    // and never double-draws a targeted one.
    const features = highlights.filter((highlight) => !hasTarget(highlight))
      .flatMap((highlight) => highlight.geojson?.features ?? []);
    if (!features.length && !targets.length) return;
    const tour = highlights.some((highlight) => highlight.action === 'tour' || highlight.action === 'flythrough');
    const label = highlights.map((highlight) => highlight.label).filter(Boolean).join(', ');
    dispatch(setChatHighlight({
      id: Date.now(),
      label,
      action: tour ? 'tour' : 'auto',
      targets,
      geojson: { type: 'FeatureCollection', features },
      // Auto-clear after 5s (like a chart click); the "Highlight again" chip re-triggers it. The
      // ChatMapHighlight stays in Redux so the chip stays available after the visual fades.
      ttlMs: 5000,
    }));
  }, [dispatch]);

  // A make_chart category was clicked → highlight those features on the map through the SAME bridge
  // show_on_map uses (setChatHighlight → the viewer renders the geometry). No new viewer wiring.
  const handleChartHighlight = useCallback((
    label: string,
    geojson: GeoJSON.FeatureCollection,
    color?: string,
    target?: { layer: string; keyField?: string; keyValues?: string[] },
  ) => {
    if (!geojson?.features?.length && !target) return;
    // A prior click still owns the highlight within its 5s lock: block re-selection until it elapses,
    // so even a click on another bar can't swap out the deliberate highlight mid-window.
    if (Date.now() < hoverLockUntilRef.current) return;
    // Prefer highlighting the displayed features in place (correct height) when the chart supplied a
    // layer + join key + the clicked category's feature keys; else fall back to drawing the geojson.
    // Transient: 5s, in the clicked bar's color.
    const targets: HighlightTarget[] | undefined = target
      ? [{ layer: target.layer, keyField: target.keyField, keyValues: target.keyValues, color }]
      : undefined;
    // This click owns the highlight for the next 5s: arm the lock so a hover OR click on any other bar
    // is ignored until it elapses — the deliberate selection can't be swapped or cleared mid-window.
    hoverLockUntilRef.current = Date.now() + CHART_HIGHLIGHT_TTL_MS;
    // action:'none': a chart click highlights features to READ them, it must not navigate the camera (shouldFrameCamera).
    dispatch(setChatHighlight({ id: Date.now(), label, action: 'none', targets, geojson, color, ttlMs: CHART_HIGHLIGHT_TTL_MS }));
  }, [dispatch]);

  // HOVERING a make_chart bar/slice previews its features on the map — same bridge, but NO ttl, so the
  // preview persists only while the cursor is over the bar; handleChartHoverClear removes it on leave.
  const handleChartHover = useCallback((
    label: string,
    geojson: GeoJSON.FeatureCollection,
    color?: string,
    target?: { layer: string; keyField?: string; keyValues?: string[] },
  ) => {
    if (!geojson?.features?.length && !target) return;
    // A click-highlight is active (its 5s lock hasn't elapsed): ignore hover so it can't override the
    // deliberate selection.
    if (Date.now() < hoverLockUntilRef.current) return;
    const targets: HighlightTarget[] | undefined = target
      ? [{ layer: target.layer, keyField: target.keyField, keyValues: target.keyValues, color }]
      : undefined;
    // action:'none': a hover PREVIEW must not navigate the camera (shouldFrameCamera) — the cursor is just resting on a bar.
    dispatch(setChatHighlight({ id: Date.now(), label, action: 'none', targets, geojson, color }));
  }, [dispatch]);
  // Cursor left a chart bar: drop the hover preview — UNLESS a click-highlight is still within its 5s
  // lock, in which case leaving a stray-hovered bar must not clear the deliberate selection.
  const handleChartHoverClear = useCallback(() => {
    if (Date.now() < hoverLockUntilRef.current) return;
    dispatch(setChatHighlight(null));
  }, [dispatch]);

  // add_spatial_layer: register each layer in the viewerLayers slice — the channel to the 3D viewer,
  // which adds a GeoJSONLayer + a "Dashboard layers" toggle. EVERY layer (any size) is written to S3
  // and carries a `key` that aw-server presigns, so the viewer streams ALL layers from the presigned
  // `url` — one copy, no size special-case. The inline `geojson` is kept ONLY as a fallback for the
  // rare case with no url (a presign miss / local dev without a bucket); when the url is present it's
  // redundant with the S3 object, so we don't hold it in the store.
  const handleSpatialLayers = useCallback((layers: ChatSpatialLayer[], refs: ChatS3Ref[]) => {
    const urlByKey = new Map(refs.map((ref) => [ref.key, ref.url]));
    layers.forEach((layer) => {
      const url = layer.key ? urlByKey.get(layer.key) ?? undefined : undefined;
      if (!layer.geojson && !url) return; // no deliverable geometry (e.g. presign failed) — skip
      dispatch(addLayer({
        id: layer.id,
        title: layer.title,
        color: layer.color,
        url,
        key: layer.key, // keep the S3 key so a saved session can re-presign this layer on restore
        // Drop the redundant inline copy when the layer streams from S3; keep it only as the no-url fallback.
        geojson: url ? undefined : layer.geojson,
        popupFields: layer.popup_fields ?? null,
        hasChart: !!layer.has_chart,
        visible: layer.visible !== false,
      }));
    });
  }, [dispatch]);

  // add_feature_analysis → per-feature analysis appended to the EXISTING feature popups (NOT a layer).
  // Keyed by layer name so buildFeatureDisplay can join it to each displayed feature.
  const handleFeatureAnalyses = useCallback(async (analyses: ChatFeatureAnalysis[], refs: ChatS3Ref[]) => {
    // The order/gdb these analyses belong to — re-checked after the async S3 rehydration so a late resolve
    // after an order switch doesn't inject this order's analyses into the newly-selected one.
    const startSessionKey = sessionKeyRef.current;
    // Rehydrate any analysis whose by_key the server OFFLOADED to S3 (over the 6 MB envelope ceiling: it
    // has `key` and an empty inline map). The presigned object is the same {join-key → chart-json} map.
    // A presign miss / fetch failure degrades to the empty map (nothing crashes); the SSRF allow-list
    // gates the URL, mirroring the file-download path.
    const urlByKey = new Map(refs.map((ref) => [ref.key, ref.url]));
    const resolved = await Promise.all(analyses.map(async (analysis) => {
      const needsRehydrate = !!analysis.key && !Object.keys(analysis.by_key || {}).length;
      const url = analysis.key ? urlByKey.get(analysis.key) : undefined;
      if (!needsRehydrate || !url || !isTrustedDownloadUrl(url)) return analysis;
      try {
        const response = await fetch(url);
        if (!response.ok) return analysis;
        const byKey = await response.json();
        return (byKey && typeof byKey === 'object') ? { ...analysis, by_key: byKey as Record<string, string> } : analysis;
      } catch {
        return analysis; // offloaded but unreachable — degrade to empty rather than throw
      }
    }));
    // Order switched during the S3 rehydration → discard; onSessionReset already cleared this order's
    // analyses, and dispatching now would bleed the previous order's analyses into the new one.
    if (sessionKeyRef.current !== startSessionKey) return;
    // onDashboard:true — computed ON: the analysis auto-pins to the dashboard and its popup section
    // shows immediately (the bubble chip + dashboard ✕ still let the user REMOVE it). setFeatureAnalysis
    // preserves an existing pin, so a recompute of an already-added analysis keeps its state.
    const byLayer: Record<string, FeatureAnalysis> = {};
    resolved.forEach((analysis) => {
      byLayer[analysis.layer] = { keyField: analysis.key_field, title: analysis.title, byKey: analysis.by_key || {}, onDashboard: true };
    });
    dispatch(setFeatureAnalysis(byLayer));
  }, [dispatch]);

  // Order/gdb switch → wipe the previous order's Khora viewer artifacts (Dashboard layers + dashboard
  // pins + map highlight + per-feature analysis) so they never bleed into the next order. Fired by
  // useChatbot before it restores the new order's saved session, which re-adds only that order's own.
  const handleSessionReset = useCallback(() => {
    dispatch(clearLayers());
    dispatch(resetDashboardArtifacts());
  }, [dispatch]);

  // go_to_pano: hand the command to EsriSplitViewer (which owns the pano viewer) via the dashboard bridge.
  const handlePano = useCallback((cmd: ChatPanoCommand) => {
    dispatch(setChatPano(cmd));
  }, [dispatch]);

  // The last tabular run_sql result → the viewer's bottom rows-panel table (null clears a stale one).
  const handleQueryResult = useCallback((queryResult: ChatQueryResult | null) => {
    dispatch(setChatQueryResult(queryResult));
  }, [dispatch]);

  // Khora-initiated map actions (trigger_app_action). In gdb-only mode only the region-of-interest actions
  // are emitted, both through the regionCommand bridge EsriSplitViewer owns: draw_region drops any prior
  // region then opens the region-draw tool; clear_region sends a {kind:'clear'} command so the viewer can
  // cancel an IN-PROGRESS draw too (nulling selectedRegion alone can't — a mid-draw region is already null).
  // Any other action is withheld by the Lambda, so it's ignored here.
  const handleActions = useCallback((actions: ChatAppAction[]) => {
    actions.forEach((appAction) => {
      if (appAction.action === 'draw_region') {
        // Drop any prior region FIRST (batched with the command) so a new draw replaces it: the
        // reconciliation effect wipes the old graphic before the fresh draw arms, so cancelling the new
        // draw leaves NO region rather than stranding the old (now-invisible) one as selected_region.
        dispatch(setSelectedRegion(null));
        dispatch(setRegionCommand({ kind: 'draw', mode: appAction.region_mode ?? 'rectangle', id: Date.now() }));
      } else if (appAction.action === 'clear_region') {
        // Route through the viewer so it can cancel an IN-PROGRESS draw too (nulling selectedRegion alone
        // can't — a mid-draw region is already null, so it's an Immer no-op that never fires reconciliation).
        dispatch(setRegionCommand({ kind: 'clear', id: Date.now() }));
      }
    });
  }, [dispatch]);

  // Pin a show_on_map / query_region highlight as a durable "Analysis Layer" on the 3D map (user-initiated).
  // Converts the highlight's geojson into a viewerLayer — the same channel add_spatial_layer uses — so the
  // Analysis Layers panel toggles/removes it. Inline geojson (no S3 key), so it renders but isn't org-shareable.
  const handlePinHighlightToMap = useCallback((highlight: ChatHighlight, id: string) => {
    const features = highlight.geojson?.features ?? [];
    if (!features.length) return;
    // Esri GeoJSONLayer holds ONE geometry type — a mixed collection (e.g. query_region returning poles as
    // Point + spans as LineString) would infer the type from the first feature and silently drop the rest.
    // Split by geometry type into one Analysis Layer per type so every feature is pinned.
    const byType = new Map<string, GeoJSON.Feature[]>();
    features.forEach((feature) => {
      const type = feature.geometry?.type;
      if (!type) return;
      const group = byType.get(type) ?? [];
      group.push(feature);
      byType.set(type, group);
    });
    const multi = byType.size > 1;
    byType.forEach((group, type) => {
      dispatch(addLayer({
        id: multi ? `${id}:${type}` : id,
        title: `${highlight.label || 'Highlighted features'}${multi ? ` · ${GEOM_TYPE_LABEL[type] ?? type}` : ''}`,
        color: highlight.color,
        geojson: { type: 'FeatureCollection', features: group },
        visible: true,
      }));
    });
  }, [dispatch]);

  // pole_storyboard fly-through: fly the camera to (and highlight, in its status color) the current scene's
  // pole via the SAME chat→map bridge show_on_map uses; no ttlMs so the highlight persists until the next
  // scene supersedes it. When panos are on, ALSO open the nearest pano at the pole. Memoized so the player's
  // scene-change effect only fires on a real scene change.
  const handleStoryboardScene = useCallback((scene: StoryboardScene, openPano: boolean) => {
    const color = STORYBOARD_STATUS_COLORS[scene.status] ?? '#9E9E9E';
    // The one-Point fallback lives INSIDE the target (drawn only when the in-place Pole_ID query resolves
    // nothing) — NOT on the top-level geojson, which would ALSO draw it beside the resolved pole, make it a
    // 2-feature highlight, and take the multi-feature union path that unions a metre-extent (3857) with a
    // degree-extent (4326) → the camera flies to a garbage location. One feature → the reprojecting goTo.
    const point = { type: 'Feature' as const, properties: { Pole_ID: scene.pole_id }, geometry: { type: 'Point' as const, coordinates: [scene.lon, scene.lat] } };
    dispatch(setChatHighlight({
      id: Date.now(),
      label: `Storyboard — pole ${scene.pole_id}`,
      action: 'auto', // must not be 'none' — that opts out of the camera fly
      color,
      targets: [{ layer: 'T_UP_BOTTOM', keyField: 'Pole_ID', keyValues: [String(scene.pole_id)], color, geojson: { type: 'FeatureCollection', features: [point] } }],
      geojson: { type: 'FeatureCollection', features: [] },
    }));
    if (openPano) dispatch(setChatPano({ label: `Pole ${scene.pole_id}`, lng: scene.lon, lat: scene.lat }));
  }, [dispatch]);

  const handleStoryboardClose = useCallback(() => {
    dispatch(setChatHighlight(null));
  }, [dispatch]);

  // Gather the non-message session artifacts (viewer layers + dashboard pins) + the S3 keys the server
  // re-presigns on load. Plain data only (no Esri objects, no raw rows) — safe to persist server-side.
  const buildSessionArtifacts = useCallback(() => {
    const state = store.getState();
    const layers = (state.viewerLayers?.layers ?? [])
      .map((layer) => ({
        id: layer.id,
        title: layer.title,
        color: layer.color,
        key: layer.key,
        geojson: layer.geojson,
        popupFields: layer.popupFields ?? null,
        hasChart: layer.hasChart,
        visible: layer.visible,
      }))
      // Keyed layers (add_spatial_layer, offloaded to S3) always persist; a keyless pin persists only if its
      // inline geojson is small enough not to jeopardize the whole session-save.
      .filter((layer) => layer.key || !layer.geojson || JSON.stringify(layer.geojson).length <= MAX_PERSISTED_INLINE_LAYER_BYTES);
    const dashboard = {
      pinnedWidgets: state.dashboard?.pinnedWidgets ?? [],
      pinnedCharts: state.dashboard?.pinnedCharts ?? [],
      // Per-feature popup analyses (add_feature_analysis) — so the "Khora Analysis" popup sections
      // survive a reload, like the pins do.
      featureAnalysis: state.dashboard?.featureAnalysis ?? {},
    };
    const s3Refs = layers.filter((layer) => layer.key).map((layer) => ({ key: layer.key as string }));
    return { session: { viewerLayers: layers, dashboard }, s3_refs: s3Refs };
  }, [store]);

  // Re-dispatch a restored session's viewer layers (offloaded ones get a fresh presigned url, joined by
  // key) + dashboard pins. Defensive: a layer with neither geojson nor a fresh url is skipped, not added.
  const onRestoreArtifacts = useCallback((session: Record<string, unknown>, s3Refs: ChatS3Ref[]) => {
    const urlByKey = new Map((s3Refs ?? []).map((ref) => [ref.key, ref.url]));
    const layers = (session.viewerLayers as ViewerLayer[] | undefined) ?? [];
    layers.forEach((layer) => {
      const url = layer.key ? urlByKey.get(layer.key) ?? undefined : layer.url;
      if (!layer.geojson && !url) return;
      dispatch(addLayer({ ...layer, url }));
    });
    const dashboard = (session.dashboard as { pinnedWidgets?: WidgetSpec[]; pinnedCharts?: PinnedChart[]; featureAnalysis?: Record<string, FeatureAnalysis> } | undefined) ?? {};
    (dashboard.pinnedWidgets ?? []).forEach((widget) => dispatch(pinWidget(widget)));
    (dashboard.pinnedCharts ?? []).forEach((chart) => dispatch(pinChart(chart)));
    if (dashboard.featureAnalysis && Object.keys(dashboard.featureAnalysis).length) {
      dispatch(setFeatureAnalysis(dashboard.featureAnalysis));
    }
  }, [dispatch]);

  // Stable per-order, PER-GDB-VERSION session identity so useChatbot resumes the right conversation
  // when the user switches orders OR versions (v3 and v5 are separate threads; switching the loaded
  // version in the viewer switches the whole conversation). Undefined until a gdb is loaded.
  const sessionKey = useAppSelector((state) => {
    const order = resolveChatOrder(state);
    const orderKey = (order?._id ?? state.project.projectId ?? undefined) as string | undefined;
    const gdbId = state.dashboard?.loadedGdbId;
    return orderKey && gdbId ? `${orderKey}:${gdbId}` : undefined;
  });
  sessionKeyRef.current = sessionKey; // keep the ref current for the async handlers above (feature-analysis rehydration)

  const {
    messages, loading, error, send, clear, progress, sessionStale, dismissStale, persistSession,
  } = useChatbot(buildAppState, getChatEndpoint, handlePinWidget, handleHighlights, handleSpatialLayers, sessionKey, buildSessionArtifacts, onRestoreArtifacts, handleFeatureAnalyses, handleSessionReset, handlePano, handleActions, handleQueryResult);

  // Re-save the session whenever the user changes the dashboard artifacts (pin / unpin / remove), so a
  // removal STICKS across a reload instead of being restored from the last-saved session (which is
  // otherwise only re-written after a chat turn). Guarded: persisting is disarmed for ~2s after each
  // session (re)load so the restore's own re-adds — and the reset-to-empty before them — can't clobber
  // the saved session; after that window it fires on genuine user changes (debounced).
  const viewerLayers = useAppSelector((state) => state.viewerLayers.layers);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  // Reactive (not a ref) so the arm TRANSITION re-runs the save effect below: a ref flip isn't reactive,
  // so an artifact removal made during the ~2s disarmed window would otherwise never be persisted (the
  // save effect saw armed=false and never re-ran when it later armed) → the removal reappears on reload.
  const [persistArmed, setPersistArmed] = useState(false);
  useEffect(() => {
    setPersistArmed(false);
    const arm = setTimeout(() => setPersistArmed(true), 2000);
    return () => clearTimeout(arm);
  }, [sessionKey]);
  useEffect(() => {
    if (!persistArmed) return undefined;
    const save = setTimeout(() => persistSession(messagesRef.current), 700);
    return () => clearTimeout(save);
  }, [persistArmed, pinnedCharts, pinnedWidgets, featureAnalysis, viewerLayers, persistSession]);

  // Decode a picked photo/PDF into a pending attachment (downscaled to the model's working size). A bad file or
  // a decode failure surfaces in the same error snackbar as downloads; it never throws.
  const handlePickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // reset so re-picking the SAME file still fires onChange
    if (!file) return;
    setAttachBusy(true);
    const result = await fileToAttachment(file);
    setAttachBusy(false);
    if (isAttachmentError(result)) { setDownloadError(result.error); return; }
    setAttachment(result);
  };

  const submit = () => {
    const typed = draft.trim();
    // Also block while a file is still decoding (attachBusy) — the send BUTTON is disabled then, but the Enter
    // path isn't, and sending now would fire before setAttachment runs, silently dropping the attachment.
    if (loading || attachBusy || (!typed && !attachment)) return;
    setDraft('');
    // An explicit upload takes precedence over the pano-vision auto-capture for this turn. Pano vision (opt-in):
    // synchronously crop the CURRENT pano view + capture the view geometry that grounds it to a pole.
    const capture = attachment ? null : (panoVisionActive ? captureCurrentPanoFrame() : null);
    const image = attachment?.block ?? capture?.image;
    const panoView = attachment ? undefined : (capture?.view ?? undefined);
    // Let an image go with no question — Khora just describes/answers about it.
    send(typed || 'What can you tell me about this?', image, panoView);
    setAttachment(null);
  };

  return (
    <>
      {!open && (
        <Fab variant="extended" aria-label="Open Khora" sx={styles.launcher} onClick={() => setOpen(true)}>
          <Box component="img" src={khoraIcon} alt="" sx={styles.launcherIcon} />
          Khora
        </Fab>
      )}
      <Drawer variant="persistent" anchor="right" open={open} PaperProps={{ sx: styles.drawerPaper }}>
        <Box sx={styles.header}>
          <Box sx={styles.title}>
            <Box component="img" src={khoraIcon} alt="" sx={{ width: 22, height: 22, borderRadius: '6px', flexShrink: 0 }} />
            <Typography variant="subtitle1">Khora</Typography>
          </Box>
          <Box sx={styles.headerActions}>
            {messages.length > 0 && (
              <IconButton
                size="small"
                aria-label="Download chat"
                sx={styles.closeButton}
                onClick={(event) => setExportMenu({ anchor: event.currentTarget, messages, scope: 'session' })}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </IconButton>
            )}
            {messages.length > 0 && (
              <IconButton
                size="small"
                aria-label="Clear chat"
                sx={styles.closeButton}
                onClick={(event) => setClearMenuEl(event.currentTarget)}
              >
                <DeleteSweepOutlinedIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" aria-label="Watch the quick tour" sx={styles.closeButton} onClick={() => setTourOpen(true)}>
              <OndemandVideoOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="Close Khora" sx={styles.closeButton} onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {chatHighlight && (
          <Box sx={{ px: 1, py: 0.5, display: 'flex', justifyContent: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleReHighlight}
              sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.2, lineHeight: 1.3 }}
            >
              ↻ Highlight again
              {chatHighlight.label ? ` · ${chatHighlight.label}` : ''}
            </Button>
          </Box>
        )}
        <Box sx={styles.messages}>
          {sessionStale && (
            <Box sx={styles.staleChip}>
              <Typography variant="caption" sx={styles.staleChipText}>
                This project’s data was updated since this chat — some results may be out of date. Ask again to refresh.
              </Typography>
              <IconButton size="small" aria-label="Dismiss" onClick={dismissStale} sx={styles.staleChipClose}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}
          {messages.length === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 1 }}>
              <Typography variant="body2" sx={styles.empty}>
                Ask a question about the geodatabase — e.g. “How many poles are there?”
              </Typography>
              <Button variant="outlined" size="small" startIcon={<OndemandVideoOutlinedIcon />} onClick={() => setTourOpen(true)}>
                Watch the quick tour
              </Button>
            </Box>
          )}
          {messages.map((message) => {
            const blocks = typeof message.content === 'string' ? null : message.content;
            const image = blocks?.find((block): block is ChatImageBlock => block.type === 'image');
            const textBlock = blocks?.find((block): block is ChatTextBlock => block.type === 'text');
            return (
              <Box
                key={message.id}
                sx={[styles.bubble, message.role === 'user' ? styles.userBubble : styles.assistantBubble]}
              >
                <ArtifactErrorBoundary label="message">
                {image && (
                  <Box
                    component="img"
                    src={`data:${image.source.media_type};base64,${image.source.data}`}
                    alt="screenshot"
                    sx={styles.messageImage}
                  />
                )}
                {typeof message.content === 'string'
                  ? (message.role === 'assistant'
                    ? <ChatMarkdown text={message.content} />
                    : <Typography variant="body2">{message.content}</Typography>)
                  : textBlock && (message.role === 'assistant'
                    ? <ChatMarkdown text={textBlock.text} />
                    : <Typography variant="body2">{textBlock.text}</Typography>)}
                {message.role === 'assistant' && (
                  <ChatAnswerTrust provenance={message.provenance} grounded={message.grounded} />
                )}
                {message.charts?.map((chart, chartIndex) => {
                  // Include the positional index: two charts in one message can share a (model-generated)
                  // title, and pinId is the React key, the chartFrameRef slot, AND the dashboard pin id —
                  // a bare message.id-title would collide across all three.
                  const pinId = `${message.id}-${chartIndex}-${chart.title}`;
                  const pinned = pinnedCharts.some((pin) => pin.id === pinId);
                  // Pin the frozen chart. For a parametric (slider) chart, snapshot ONLY the current stop:
                  // freeze its frame as `data`, drop param/frames, and title it with the value.
                  const pinChartNow = () => {
                    if (chart.param && chart.frames && chart.frames.length) {
                      const index = Math.min(chartFrameRef.current[pinId] ?? chart.param.default_index ?? 0, chart.frames.length - 1);
                      const value = chart.param.values[index];
                      const label = `${chart.param.name} ${value}${chart.param.unit ? ` ${chart.param.unit}` : ''}`;
                      handlePinChart(pinId, { ...chart, data: chart.frames[index] ?? chart.data, param: undefined, frames: undefined, title: `${chart.title} · ${label}` });
                    } else {
                      handlePinChart(pinId, chart);
                    }
                  };
                  return (
                    <ArtifactErrorBoundary key={pinId} label="chart">
                      <ChatChart chart={chart} onHighlight={handleChartHighlight} onHoverHighlight={handleChartHover} onClearHighlight={handleChartHoverClear} onFrameChange={(index) => { chartFrameRef.current[pinId] = index; }} />
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Button size="small" onClick={() => (pinned ? handleUnpinChart(pinId) : pinChartNow())}>
                          {pinned ? 'Unpin from dashboard' : 'Pin to dashboard'}
                        </Button>
                        {pinned && !!chart.param && !!chart.frames?.length && (
                          // Slider moved after pinning? Re-snapshot the pinned copy at the current value
                          // (pinChart replaces on the same id) — no unpin/re-pin dance.
                          <Button size="small" onClick={pinChartNow}>
                            Update pinned chart to current value
                          </Button>
                        )}
                      </Box>
                    </ArtifactErrorBoundary>
                  );
                })}
                {message.feature_analyses?.map((analysis) => {
                  const onDashboard = featureAnalysis[analysis.layer]?.onDashboard ?? false;
                  const count = Object.keys(analysis.by_key || {}).length;
                  const label = analysis.title || `${analysis.layer} analysis`;
                  return (
                    <Button
                      key={`fa-${analysis.layer}`}
                      size="small"
                      startIcon={onDashboard ? <CloseIcon sx={{ fontSize: 13 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 13 }} />}
                      onClick={() => (onDashboard
                        ? dispatch(removeFeatureAnalysis(analysis.layer))
                        : dispatch(setFeatureAnalysisOnDashboard({ layer: analysis.layer, onDashboard: true })))}
                    >
                      {onDashboard
                        ? `Remove "${label}" from dashboard`
                        : `Add "${label}" to dashboard (${count} features)`}
                    </Button>
                  );
                })}
                {message.widgets?.map((widget) => {
                  const id = widgetSpecId(widget);
                  const pinned = pinnedWidgets.some((pin) => pin.id === id);
                  const previewData = widgetPreviewData(
                    { operation: widget.operation as WidgetOperation, kind: widget.kind as WidgetKind, byField: widget.by_field, field: widget.field, displayUnit: widget.display_unit },
                    widget.preview,
                  );
                  return (
                    <ArtifactErrorBoundary key={`w-${id}`} label="widget">
                      {/* Dark card matching the dashboard's, so the white widget text/value reads in the
                          light chat bubble (WidgetView is styled for the dark dashboard surface). */}
                      <Box sx={{ ...styles.chartBlock, backgroundColor: 'rgba(21, 59, 90, 0.96)', borderRadius: 1, p: 1, alignSelf: 'stretch' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'common.white' }}>{widget.title}</Typography>
                          {widget.derivation && (
                            <MuiTooltip title={widget.derivation}>
                              <IconButton size="small" sx={{ color: 'grey.400', p: 0.25 }} aria-label="How this widget is computed">
                                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </MuiTooltip>
                          )}
                        </Box>
                        {previewData
                          ? <WidgetView data={previewData} />
                          : <Typography sx={{ fontSize: '0.75rem', color: 'grey.400', py: 1 }}>{`${widget.operation}${widget.field ? ` · ${widget.field}` : ''} · ${widget.layers.join(', ')}`}</Typography>}
                        <Button
                          size="small"
                          startIcon={pinned ? <CloseIcon sx={{ fontSize: 13 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 13 }} />}
                          onClick={() => (pinned ? handleUnpinWidget(id) : handlePinWidget(widget))}
                        >
                          {pinned ? 'Remove widget from dashboard' : 'Pin as widget to dashboard'}
                        </Button>
                      </Box>
                    </ArtifactErrorBoundary>
                  );
                })}
                {message.cards?.map((card) => (
                  <ArtifactErrorBoundary key={card.title ?? card.type} label="card">
                    {/* Dark card matching the dashboard's, so the white card text reads in the light bubble. */}
                    <Box sx={{ ...styles.chartBlock, backgroundColor: 'rgba(21, 59, 90, 0.96)', borderRadius: 1, p: 1, alignSelf: 'stretch' }}>
                      {card.title && (
                        <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'common.white', mb: 0.5 }}>{card.title}</Typography>
                      )}
                      {card.type === 'pole_storyboard' && (
                        <StoryboardPlayer card={card} onScene={handleStoryboardScene} onClose={handleStoryboardClose} />
                      )}
                      {card.type === 're_tension' && <ReTensionSlider card={card} />}
                      {card.type !== 'pole_storyboard' && card.type !== 're_tension' && <ChatCard card={card} />}
                    </Box>
                  </ArtifactErrorBoundary>
                ))}
                {message.highlights?.map((highlight, index) => {
                  if (!highlight.geojson?.features?.length) return null;
                  const layerId = highlightLayerId(highlight, message.id, index);
                  // A mixed-geometry pin splits into `${layerId}:<type>` layers, so match those too.
                  const onMap = viewerLayers.some((layer) => layer.id === layerId || layer.id.startsWith(`${layerId}:`));
                  return (
                    <Button
                      key={layerId}
                      size="small"
                      startIcon={<PushPinOutlinedIcon sx={{ fontSize: 13 }} />}
                      disabled={onMap}
                      sx={styles.fileDownload}
                      onClick={() => handlePinHighlightToMap(highlight, layerId)}
                    >
                      {onMap ? 'On map' : `Pin to map (${highlight.geojson.features.length})`}
                    </Button>
                  );
                })}
                {message.files?.map((file) => (
                  <Button
                    key={file.name}
                    size="small"
                    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
                    sx={styles.fileDownload}
                    onClick={() => handleDownloadFile(file)}
                  >
                    {file.name}
                  </Button>
                ))}
                {message.role === 'assistant' && (
                  <IconButton
                    size="small"
                    aria-label="Download this response"
                    sx={styles.messageDownload}
                    onClick={(event) => setExportMenu({ anchor: event.currentTarget, messages: [message], scope: 'message' })}
                  >
                    <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
                </ArtifactErrorBoundary>
              </Box>
            );
          })}
          {loading && <ThinkingIndicator styles={styles} status={progress} />}
        </Box>

        {error && <Typography variant="caption" sx={styles.error}>{error}</Typography>}

        <Menu
          anchorEl={exportMenu?.anchor}
          open={Boolean(exportMenu)}
          onClose={() => setExportMenu(null)}
          sx={{ zIndex: theme.zIndex.modal + 3 }}
        >
          {([['md', 'Markdown (.md)'], ['html', 'HTML (.html)'], ['txt', 'Text (.txt)'], ['json', 'JSON (.json)']] as [ExportFormat, string][]).map(([format, label]) => (
            <MenuItem
              key={format}
              onClick={() => {
                if (exportMenu) exportChat(exportMenu.messages, format, exportMenu.scope);
                setExportMenu(null);
              }}
            >
              {label}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          anchorEl={clearMenuEl}
          open={Boolean(clearMenuEl)}
          onClose={() => setClearMenuEl(null)}
          sx={{ zIndex: theme.zIndex.modal + 3 }}
        >
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              clear();
              setClearMenuEl(null);
            }}
          >
            Clear conversation
          </MenuItem>
        </Menu>

        {attachment ? (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, mx: 1, mb: 0.5, px: 0.75, py: 0.5,
            borderRadius: 1, bgcolor: 'action.hover', maxWidth: '100%',
          }}
          >
            <Box component="img" src={attachment.previewUrl} alt=""
              sx={{ width: 34, height: 34, borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
            />
            <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {attachment.kind === 'pdf' ? `PDF · ${attachment.name}` : attachment.name}
            </Typography>
            <IconButton size="small" aria-label="Remove attachment" onClick={() => setAttachment(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : attachBusy ? (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, mx: 1, mb: 0.5, fontSize: 12, color: 'text.secondary',
          }}
          >
            <CircularProgress size={14} /> Reading file…
          </Box>
        ) : panoVisionActive ? (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, mx: 1, mb: 0.5,
            fontSize: 12, fontFamily: 'monospace', color: 'primary.main',
          }}
          >
            <span role="img" aria-label="panorama">📷</span>
            Pano attached — Khora will analyze the current view
          </Box>
        ) : null}
        <Box sx={styles.inputRow}>
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf" hidden onChange={handlePickFile} />
          <MuiTooltip title="Attach a photo or PDF page">
            <span>
              <IconButton
                aria-label="Attach a photo or PDF"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || attachBusy}
              >
                <AttachFileOutlinedIcon />
              </IconButton>
            </span>
          </MuiTooltip>
          <TextField
            fullWidth
            size="small"
            placeholder="Ask about the geodatabase…"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            disabled={loading}
          />
          <IconButton color="primary" aria-label="Send" onClick={submit} disabled={loading || (!draft.trim() && !attachment)}>
            <SendIcon />
          </IconButton>
        </Box>
        <Typography sx={{ px: 2, pb: 1, pt: 0.25, fontSize: '0.66rem', color: 'text.secondary', textAlign: 'center' }}>
          Khora can make mistakes. Use it with caution.
        </Typography>
        <Snackbar
          open={!!downloadError}
          autoHideDuration={5000}
          onClose={() => setDownloadError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="warning" variant="filled" onClose={() => setDownloadError(null)} sx={{ fontSize: '0.78rem' }}>
            {downloadError}
          </Alert>
        </Snackbar>
      </Drawer>
      <Dialog
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#153B5A', borderRadius: 2, overflow: 'hidden' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#FFFFFF' }}>Khora — quick tour</Typography>
          <IconButton size="small" aria-label="Close tour" onClick={() => setTourOpen(false)} sx={{ color: '#B9E2F1' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {tourOpen && (
          <video src={tourVideo} controls autoPlay muted playsInline style={{ width: '100%', display: 'block' }}>
            <track kind="captions" />
          </video>
        )}
      </Dialog>
    </>
  );
};

// Mount gate: isKhoraUiEnabled (./constants) — master kill switch + pilot-project name + the
// ?khora=off escape hatch, shared with the viewer's Khora companions (pano chip + looking-angle
// readout, gdb-rows table, region chip, output Dashboard) so every Khora surface gates identically.
// The gate lives OUTSIDE the panel component so a non-pilot project mounts NOTHING — none of the
// chat's hooks (session restore, shared-pins fetch) run. Mounted by the Project Viewer
// (EsriSplitViewer), Khora's only page.
const KhoraChatbot = () => {
  const projectName = useAppSelector((state) => state.project.project?.name);
  if (!isKhoraUiEnabled(projectName)) return null;
  return <ChatbotLauncher />;
};

export default KhoraChatbot;
